import { WorkspaceService } from "@/features/Workspaces/services/workspaceService";
import { IntegrationBaseService } from "@/features/Integrations/services/integrationsBaseService";
import { AgentBaseService } from "@/features/Agents/services/agentBaseService";
import { get } from "@/shared/utils/fetchApi";

export type ValidatorKey =
  | "hasCreatedProduct"
  | "hasUploadedDocument"
  | "hasCreatedIntegration"
  | "hasInstalledAgent"
  | "hasSentMessage";

export const TOUR_VALIDATORS: Record<ValidatorKey, () => Promise<boolean>> = {
  hasCreatedProduct: async () => {
    try {
      const products = await WorkspaceService.getAll();
      const result = products.length > 0;
      return result;
    } catch (error) {
      console.error("[tourValidators] hasCreatedProduct - Error:", error);
      return false;
    }
  },

  hasUploadedDocument: async () => {
    try {
      // Busca o produto atual
      const products = await WorkspaceService.getAll();
      if (!products || products.length === 0) {
        return false;
      }

      const currentWorkspace = products[0]; // Primeiro produto
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const knowledgeResponse = await get<{ data: any[] }>(
        `/knowledge-bases/by-product/${currentWorkspace.id}`,
      );
      if (
        !knowledgeResponse ||
        !knowledgeResponse.data ||
        knowledgeResponse.data.length === 0
      ) {
        return false;
      }

      const knowledgeBase = knowledgeResponse.data[0];
      if (!knowledgeBase || !knowledgeBase.id) {
        return false;
      }

      // Verifica se há arquivos no knowledge base
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const storageResponse = await get<{ data: any[] }>(
        `/knowledge-bases/${knowledgeBase.id}/storage`,
      );
      const hasDocuments =
        storageResponse &&
        storageResponse.data &&
        storageResponse.data.length > 0;
      return hasDocuments;
    } catch (error) {
      console.error("[tourValidators] hasUploadedDocument - Error:", error);
      return false;
    }
  },

  hasCreatedIntegration: async () => {
    try {
      // Integrações ativas da organização — a organização vem do token.
      const integrationsBase = await IntegrationBaseService.getAll({
        status: "active",
      });
      const currentCount = integrationsBase.length;

      // Busca contagem inicial do sessionStorage
      const initialCountKey = "tour_integrations_initial_count";
      const initialCountStr = sessionStorage.getItem(initialCountKey);

      if (initialCountStr === null) {
        return false;
      }

      const initialCount = parseInt(initialCountStr, 10);
      if (Number.isNaN(initialCount)) {
        // Valor corrompido no storage: sem a guarda, `currentCount > NaN` é
        // sempre false e o passo do tour nunca completava.
        return false;
      }
      const hasIncreased = currentCount > initialCount;

      return hasIncreased;
    } catch (error) {
      console.error("[tourValidators] hasCreatedIntegration - Error:", error);
      return false;
    }
  },

  hasInstalledAgent: async () => {
    try {
      // Busca produtos para pegar o product_id
      const products = await WorkspaceService.getAll();
      if (!products || products.length === 0) {
        return false;
      }

      const currentWorkspace = products[0];

      // Busca agentes do produto
      const response = await AgentBaseService.getByProductId(
        currentWorkspace.id,
      );
      if (!response || !response.data) {
        return false;
      }

      const currentCount = response.data.length;

      // Busca contagem inicial do sessionStorage
      const initialCountKey = "tour_agents_initial_count";
      const initialCountStr = sessionStorage.getItem(initialCountKey);

      if (initialCountStr === null) {
        return false;
      }

      const initialCount = parseInt(initialCountStr, 10);
      if (Number.isNaN(initialCount)) {
        // Valor corrompido no storage: sem a guarda, `currentCount > NaN` é
        // sempre false e o passo do tour nunca completava.
        return false;
      }
      const hasIncreased = currentCount > initialCount;

      return hasIncreased;
    } catch (error) {
      console.error("[tourValidators] hasInstalledAgent - Error:", error);
      return false;
    }
  },

  hasSentMessage: async () => {
    try {
      // Estratégia simples: verificar se interagiu e já passou tempo suficiente
      const interactionTime = sessionStorage.getItem(
        "tour_playground_interaction_time",
      );

      if (!interactionTime) {
        return false;
      }

      const interactedAt = parseInt(interactionTime, 10);
      if (Number.isNaN(interactedAt)) {
        return false;
      }
      const now = Date.now();
      const elapsed = now - interactedAt;

      // Se já passaram 15 segundos desde a interação, considerar completo
      // (tempo suficiente para enviar mensagem e receber resposta)
      const isComplete = elapsed >= 15000;

      return isComplete;
    } catch (error) {
      console.error("[tourValidators] hasSentMessage - Error:", error);
      return false;
    }
  },
};

export const getValidator = (key: string): (() => Promise<boolean>) => {
  if (key in TOUR_VALIDATORS) {
    return TOUR_VALIDATORS[key as ValidatorKey];
  }

  // Fallback: sempre retorna false para validadores desconhecidos
  return async () => false;
};
